import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, Landmark, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { Fragment, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  borrarMovimientoBancario,
  borrarMovimientosBancarios,
  cargarMovimientosBancarios,
  descargarMovimientosBancariosXlsx,
  crearMovimientoBancario,
  editarMovimientoBancario,
  editarSaldosCuenta,
  getMovimientosBancarios,
  type CargaMovimientos,
  type CuentaBancaria,
  type MovimientoBancario,
  type MovimientoBancarioIn,
} from "@/lib/api";

const num = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fechaCorta = (iso: string) => {
  const [a, m, d] = iso.split("-").map(Number);
  return new Date(a, m - 1, d).toLocaleDateString("es-MX", {
    day: "2-digit", month: "short", year: "2-digit",
  });
};

/** Siguiente ID con el mismo prefijo y ancho que el mayor existente: MB-240 → MB-241. */
function siguienteId(items: MovimientoBancario[]): string {
  let mejor: { pref: string; n: number; ancho: number } | null = null;
  for (const m of items) {
    const r = /^(.*?)(\d+)$/.exec(m.id_movimiento);
    if (r && (!mejor || Number(r[2]) > mejor.n)) {
      mejor = { pref: r[1], n: Number(r[2]), ancho: r[2].length };
    }
  }
  if (!mejor) return "MB-001";
  return mejor.pref + String(mejor.n + 1).padStart(mejor.ancho, "0");
}

type Borrador = {
  id_movimiento: string;
  fecha_pago: string;
  concepto: string;
  factura_relacionada: string;
  tipo: string;
  inversion: string;
  cuenta: string;
  monto: string;
  moneda: string;
};

const aBorrador = (m: MovimientoBancario): Borrador => ({
  id_movimiento: m.id_movimiento,
  fecha_pago: m.fecha_pago,
  concepto: m.concepto ?? "",
  factura_relacionada: m.factura_relacionada ?? "",
  tipo: m.tipo,
  inversion: m.inversion ?? "",
  cuenta: m.cuenta,
  monto: m.monto == null ? "" : String(m.monto),
  moneda: m.moneda,
});

const aPayload = (b: Borrador): MovimientoBancarioIn => ({
  id_movimiento: b.id_movimiento.trim(),
  fecha_pago: b.fecha_pago,
  concepto: b.concepto.trim() || null,
  factura_relacionada: b.factura_relacionada.trim() || null,
  tipo: b.tipo,
  inversion: b.inversion || null,
  cuenta: b.cuenta,
  monto: b.monto.trim() === "" ? null : Number(b.monto.replace(/,/g, "")),
  moneda: b.moneda,
});

/*
 * Operación → Contabilidad → Movimientos bancarios. Carga INCREMENTAL de Excel
 * (upsert por ID Movimiento, nunca borra) con previa antes de aplicar, más
 * captura y edición manual en la tabla.
 */
export function MovimientosBancariosPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<CargaMovimientos | null>(null);
  const [editando, setEditando] = useState<string | null>(null); // id o "__nuevo__"
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());

  const [fCuenta, setFCuenta] = useState("");
  const [fTipo, setFTipo] = useState("");
  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");
  const [fTexto, setFTexto] = useState("");
  const [fInversion, setFInversion] = useState(""); // "" todos · "-" sin inversión · Abono · Retiro

  const q = useQuery({ queryKey: ["movimientos-bancarios"], queryFn: getMovimientosBancarios });
  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const cuentas = q.data?.cuentas ?? [];

  const invalidar = () => qc.invalidateQueries({ queryKey: ["movimientos-bancarios"] });
  const err = (e: unknown, def: string) => setError(e instanceof Error ? e.message : def);

  const carga = useMutation({
    mutationFn: (p: { f: File; confirmar: boolean }) => cargarMovimientosBancarios(p.f, p.confirmar),
    onSuccess: (r) => {
      setError(null);
      if (r.aplicado) {
        setPrevia(null);
        setArchivo(null);
        setAviso(
          `Carga aplicada: ${r.nuevos} nuevos, ${r.actualizados} actualizados, ` +
            `${r.sin_cambio} sin cambio` +
            (r.errores.length ? `, ${r.errores.length} renglones descartados` : "") + ".",
        );
        invalidar();
      } else {
        setAviso(null);
        setPrevia(r);
      }
    },
    onError: (e) => err(e, "No se pudo leer el archivo"),
  });

  const guardar = useMutation({
    mutationFn: (p: { nuevo: boolean; id: string; b: Borrador }) =>
      p.nuevo
        ? crearMovimientoBancario(aPayload(p.b))
        : editarMovimientoBancario(p.id, aPayload(p.b)),
    onSuccess: (m) => {
      setError(null);
      setEditando(null);
      setBorrador(null);
      setAviso(`Movimiento ${m.id_movimiento} guardado.`);
      invalidar();
    },
    onError: (e) => err(e, "No se pudo guardar"),
  });

  const borrar = useMutation({
    mutationFn: (id: string) => borrarMovimientoBancario(id),
    onSuccess: (_r, id) => {
      setAviso(`Movimiento ${id} borrado.`);
      invalidar();
    },
    onError: (e) => err(e, "No se pudo borrar"),
  });

  const borrarVarios = useMutation({
    mutationFn: (ids: string[]) => borrarMovimientosBancarios(ids),
    onSuccess: (r) => {
      setError(null);
      setSel(new Set());
      setAviso(`${r.eliminados} movimiento${r.eliminados === 1 ? "" : "s"} borrado${r.eliminados === 1 ? "" : "s"}.`);
      invalidar();
    },
    onError: (e) => err(e, "No se pudo borrar"),
  });

  const onArchivo = (f: File | null) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    setArchivo(f);
    carga.mutate({ f, confirmar: false });
  };

  const empezarNuevo = () => {
    const c = cuentas[0];
    setEditando("__nuevo__");
    setBorrador({
      id_movimiento: siguienteId(items),
      fecha_pago: new Date().toLocaleDateString("en-CA"),
      concepto: "",
      factura_relacionada: "",
      tipo: "Egreso",
      inversion: "",
      cuenta: c?.clave ?? "",
      monto: "",
      moneda: c?.moneda ?? "MXN",
    });
  };

  const filtrados = useMemo(() => {
    const t = fTexto.trim().toLowerCase();
    return items.filter(
      (m) =>
        (!fCuenta || m.cuenta === fCuenta) &&
        (!fTipo || m.tipo === fTipo) &&
        (!fDesde || m.fecha_pago >= fDesde) &&
        (!fHasta || m.fecha_pago <= fHasta) &&
        (!fInversion || (fInversion === "-" ? m.inversion == null : m.inversion === fInversion)) &&
        (!t ||
          m.id_movimiento.toLowerCase().includes(t) ||
          (m.concepto ?? "").toLowerCase().includes(t) ||
          (m.factura_relacionada ?? "").toLowerCase().includes(t)),
    );
  }, [items, fCuenta, fTipo, fDesde, fHasta, fInversion, fTexto]);

  // Totales del filtro por moneda (sin mezclar MXN con USD).
  const totales = useMemo(() => {
    const t: Record<string, { ingreso: number; egreso: number }> = {};
    for (const m of filtrados) {
      if (m.monto == null) continue;
      const k = (t[m.moneda] ??= { ingreso: 0, egreso: 0 });
      if (m.tipo === "Ingreso") k.ingreso += m.monto;
      else k.egreso += m.monto;
    }
    return t;
  }, [filtrados]);

  const hayFiltro = fCuenta || fTipo || fDesde || fHasta || fInversion || fTexto;
  const ocupado = guardar.isPending;

  // Excel con lo visible: todos sin filtro, o solo lo filtrado.
  const descarga = useMutation({
    mutationFn: () =>
      descargarMovimientosBancariosXlsx(hayFiltro ? filtrados.map((m) => m.id_movimiento) : null),
    onError: (e) => err(e, "No se pudo descargar"),
  });

  // Selección: solo cuenta lo visible con el filtro actual.
  const selVisibles = filtrados.filter((m) => sel.has(m.id_movimiento)).map((m) => m.id_movimiento);
  const todosSel = filtrados.length > 0 && selVisibles.length === filtrados.length;
  const alternarTodos = () =>
    setSel(todosSel ? new Set() : new Set(filtrados.map((m) => m.id_movimiento)));
  const alternar = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const borrarSeleccion = () => {
    const n = selVisibles.length;
    if (window.confirm(`¿Borrar ${n} movimiento${n === 1 ? "" : "s"}? No se puede deshacer.`)) {
      borrarVarios.mutate(selVisibles);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Landmark className="w-5 h-5 text-slate-500" /> Movimientos bancarios
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Sube el Excel con columnas <b>ID Movimiento | Fecha de Pago | Concepto | Factura
            Relacionada | Tipo | Inversión | Cuenta | Monto | Moneda</b>. La carga es{" "}
            <b>incremental</b>: no se borra nada de lo ya cargado; si el ID Movimiento ya existe,
            se reemplazan sus valores. Antes de aplicar verás qué es nuevo y qué cambia.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={(e) => onArchivo(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={empezarNuevo}
            disabled={editando !== null || cuentas.length === 0}
            className="inline-flex items-center gap-2 border text-slate-700 rounded px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Nuevo movimiento
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={carga.isPending}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> {carga.isPending ? "Leyendo…" : "Cargar Excel"}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm flex justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {aviso && !previa && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">
          {aviso}
        </div>
      )}

      {previa && archivo && (
        <div className="bg-white border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="text-sm text-slate-700">
            Previa de <b>{archivo.name}</b> — {previa.leidos} renglones leídos.
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <Chip color="emerald" n={previa.nuevos} label="nuevos" />
            <Chip color="amber" n={previa.actualizados} label="se actualizan" />
            <Chip color="slate" n={previa.sin_cambio} label="sin cambio" />
            {previa.errores.length > 0 && (
              <Chip color="red" n={previa.errores.length} label="se descartan" />
            )}
          </div>
          {previa.ids_actualizados.length > 0 && (
            <div className="text-xs text-slate-600">
              <b>Se reemplazan:</b> {previa.ids_actualizados.join(", ")}
              {previa.actualizados > previa.ids_actualizados.length && " …"}
            </div>
          )}
          {previa.sin_monto.length > 0 && (
            <div className="text-xs text-amber-700">
              <b>Sin monto numérico</b> (se guardan con monto vacío): {previa.sin_monto.join(", ")}
            </div>
          )}
          {previa.sin_concepto.length > 0 && (
            <div className="text-xs text-amber-700">
              <b>Sin concepto</b> (se guardan con concepto vacío): {previa.sin_concepto.join(", ")}
            </div>
          )}
          {previa.duplicados.length > 0 && (
            <div className="text-xs text-amber-700">
              <b>ID repetido en el archivo</b> (se queda el último renglón):{" "}
              {previa.duplicados.join(", ")}
            </div>
          )}
          {previa.errores.length > 0 && (
            <div className="text-xs text-red-700 max-h-40 overflow-y-auto border border-red-100 rounded p-2 bg-red-50">
              {previa.errores.map((e) => (
                <div key={`${e.renglon}-${e.id_movimiento}`}>
                  Renglón {e.renglon} ({e.id_movimiento}): {e.motivo}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={carga.isPending || previa.nuevos + previa.actualizados === 0}
              onClick={() => carga.mutate({ f: archivo, confirmar: true })}
              className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              {carga.isPending ? "Aplicando…" : `Aplicar (${previa.nuevos + previa.actualizados})`}
            </button>
            <button
              type="button"
              onClick={() => {
                setPrevia(null);
                setArchivo(null);
              }}
              className="border text-slate-600 rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Resumen por cuenta (de TODOS los movimientos, no del filtro) */}
      {cuentas.length > 0 && (
        <TablaCuentas
          cuentas={cuentas}
          tc={q.data?.tipo_cambio.mxn_por_usd ?? null}
          fCuenta={fCuenta}
          onFiltrar={(clave) => setFCuenta(fCuenta === clave ? "" : clave)}
          onError={(e) => err(e, "No se pudo guardar")}
          onGuardado={invalidar}
        />
      )}

      {/* Filtros */}
      <div className="bg-white border rounded-lg p-3 flex flex-wrap items-end gap-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Cuenta</span>
          <select value={fCuenta} onChange={(e) => setFCuenta(e.target.value)} className="border rounded px-2 py-1">
            <option value="">Todas</option>
            {cuentas.map((c) => (
              <option key={c.clave} value={c.clave}>{c.clave}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Tipo</span>
          <select value={fTipo} onChange={(e) => setFTipo(e.target.value)} className="border rounded px-2 py-1">
            <option value="">Todos</option>
            <option>Ingreso</option>
            <option>Egreso</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Desde</span>
          <input type="date" value={fDesde} onChange={(e) => setFDesde(e.target.value)} className="border rounded px-2 py-1" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Hasta</span>
          <input type="date" value={fHasta} onChange={(e) => setFHasta(e.target.value)} className="border rounded px-2 py-1" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Inversión</span>
          <select value={fInversion} onChange={(e) => setFInversion(e.target.value)} className="border rounded px-2 py-1">
            <option value="">Todos</option>
            <option value="-">-</option>
            <option value="Abono">Abonos</option>
            <option value="Retiro">Retiros</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <span className="text-xs text-slate-500">Buscar (ID, concepto, factura)</span>
          <input value={fTexto} onChange={(e) => setFTexto(e.target.value)} className="border rounded px-2 py-1" />
        </label>
        <button
          type="button"
          onClick={() => descarga.mutate()}
          disabled={descarga.isPending || filtrados.length === 0}
          title={hayFiltro ? "Descarga solo los movimientos filtrados" : "Descarga todos los movimientos"}
          className="inline-flex items-center gap-2 border text-slate-700 rounded px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {descarga.isPending ? "Generando…" : hayFiltro ? `Descargar Excel (${filtrados.length})` : "Descargar Excel"}
        </button>
        {hayFiltro && (
          <button
            type="button"
            onClick={() => {
              setFCuenta(""); setFTipo(""); setFDesde(""); setFHasta(""); setFInversion(""); setFTexto("");
            }}
            className="text-xs text-blue-600 hover:underline pb-1.5"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          {(q.error as Error).message}
        </div>
      )}

      {!q.isLoading && !q.isError && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="pl-3 pr-1 py-2 w-8">
                  <input
                    type="checkbox"
                    title={todosSel ? "Quitar selección" : "Seleccionar todos (los visibles con el filtro)"}
                    checked={todosSel}
                    ref={(el) => {
                      if (el) el.indeterminate = selVisibles.length > 0 && !todosSel;
                    }}
                    disabled={filtrados.length === 0 || editando !== null}
                    onChange={alternarTodos}
                    className="align-middle cursor-pointer"
                  />
                </th>
                <th className="px-3 py-2">ID Movimiento</th>
                <th className="px-3 py-2">Fecha de pago</th>
                <th className="px-3 py-2">Concepto</th>
                <th className="px-3 py-2">Factura rel.</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Inversión</th>
                <th className="px-3 py-2">Cuenta</th>
                <th className="px-3 py-2 text-right">Monto</th>
                <th className="px-3 py-2">Moneda</th>
                <th className="px-2 py-2 w-16 whitespace-nowrap">
                  {selVisibles.length > 0 && (
                    <button
                      type="button"
                      title="Borrar los movimientos seleccionados"
                      disabled={borrarVarios.isPending || editando !== null}
                      onClick={borrarSeleccion}
                      className="inline-flex items-center gap-1 bg-red-600 text-white rounded px-2 py-1 text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {borrarVarios.isPending ? "Borrando…" : `Eliminar (${selVisibles.length})`}
                    </button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {editando === "__nuevo__" && borrador && (
                <FilaEdicion
                  nuevo
                  b={borrador}
                  cuentas={cuentas}
                  onChange={setBorrador}
                  ocupado={ocupado}
                  onGuardar={() => guardar.mutate({ nuevo: true, id: borrador.id_movimiento, b: borrador })}
                  onCancelar={() => { setEditando(null); setBorrador(null); }}
                />
              )}
              {filtrados.map((m) =>
                editando === m.id_movimiento && borrador ? (
                  <FilaEdicion
                    key={m.id_movimiento}
                    b={borrador}
                    cuentas={cuentas}
                    onChange={setBorrador}
                    ocupado={ocupado}
                    onGuardar={() => guardar.mutate({ nuevo: false, id: m.id_movimiento, b: borrador })}
                    onCancelar={() => { setEditando(null); setBorrador(null); }}
                  />
                ) : (
                  <tr
                    key={m.id_movimiento}
                    className={`border-t hover:bg-slate-50 ${sel.has(m.id_movimiento) ? "bg-red-50/60" : ""}`}
                  >
                    <td className="pl-3 pr-1 py-1.5">
                      <input
                        type="checkbox"
                        checked={sel.has(m.id_movimiento)}
                        disabled={editando !== null}
                        onChange={() => alternar(m.id_movimiento)}
                        className="align-middle cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{m.id_movimiento}</td>
                    <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{fechaCorta(m.fecha_pago)}</td>
                    <td className="px-3 py-1.5">{m.concepto ?? <span className="text-amber-600" title="Sin concepto">—</span>}</td>
                    <td className="px-3 py-1.5 text-slate-600">{m.factura_relacionada ?? "—"}</td>
                    <td className="px-3 py-1.5">
                      <span
                        className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 ${
                          m.tipo === "Ingreso" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {m.tipo}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-slate-600">{m.inversion ?? "—"}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{m.cuenta}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
                      {m.monto == null ? <span className="text-amber-600" title="Sin monto">—</span> : num(m.monto)}
                    </td>
                    <td className="px-3 py-1.5">{m.moneda}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <button
                        type="button"
                        title="Editar"
                        disabled={editando !== null}
                        onClick={() => { setEditando(m.id_movimiento); setBorrador(aBorrador(m)); }}
                        className="text-slate-400 hover:text-blue-600 disabled:opacity-30 mr-2"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title="Borrar"
                        disabled={editando !== null}
                        onClick={() => {
                          if (window.confirm(`¿Borrar el movimiento ${m.id_movimiento}?`)) borrar.mutate(m.id_movimiento);
                        }}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ),
              )}
              {filtrados.length === 0 && editando !== "__nuevo__" && (
                <tr>
                  <td colSpan={11}className="px-3 py-8 text-center text-slate-500">
                    {items.length === 0
                      ? "Sin movimientos — carga el Excel o captura uno nuevo."
                      : "Ningún movimiento con estos filtros."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="px-3 py-2 border-t text-xs text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
            <span>
              {filtrados.length}
              {hayFiltro ? ` de ${items.length}` : ""} movimientos
            </span>
            {Object.entries(totales).map(([mon, t]) => (
              <span key={mon} className="tabular-nums">
                <b>{mon}</b>: ingresos {num(t.ingreso)} · egresos {num(t.egreso)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Columnas numéricas del resumen; "total" = inversión final (inicial + abonos − retiros). */
const COLS_CUENTA: { k: keyof CuentaBancaria; label: string; fuerte?: boolean }[] = [
  { k: "saldo_inicial", label: "Saldo inicial" },
  { k: "ingresos", label: "Ingresos" },
  { k: "egresos", label: "Egresos" },
  { k: "saldo", label: "Saldo Final", fuerte: true },
  { k: "inversion_inicial", label: "Inversión inicial" },
  { k: "abonos", label: "Abonos" },
  { k: "retiros", label: "Retiros" },
  { k: "inversion", label: "Total", fuerte: true },
];

function TablaCuentas({
  cuentas,
  tc,
  fCuenta,
  onFiltrar,
  onError,
  onGuardado,
}: {
  cuentas: CuentaBancaria[];
  tc: number | null; // MXN por USD (Configuración → Tipo de Cambio)
  fCuenta: string;
  onFiltrar: (clave: string) => void;
  onError: (e: unknown) => void;
  onGuardado: () => void;
}) {
  const [editando, setEditando] = useState<string | null>(null);
  const [saldoIni, setSaldoIni] = useState("");
  const [invIni, setInvIni] = useState("");
  const guardar = useMutation({
    mutationFn: (clave: string) =>
      editarSaldosCuenta(clave, {
        saldo_inicial: Number(saldoIni.replace(/,/g, "")) || 0,
        inversion_inicial: Number(invIni.replace(/,/g, "")) || 0,
      }),
    onSuccess: () => {
      setEditando(null);
      onGuardado();
    },
    onError,
  });

  // Sumatoria por moneda: MXN y USD no se mezclan.
  const monedas = [...new Set(cuentas.map((c) => c.moneda))];
  const suma = (mon: string, k: keyof CuentaBancaria) =>
    cuentas.filter((c) => c.moneda === mon).reduce((a, c) => a + (c[k] as number), 0);

  const celda = (v: number, fuerte?: boolean) => (
    <td
      className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${fuerte ? "font-semibold" : ""} ${
        v < 0 ? "text-red-600" : fuerte ? "text-slate-900" : "text-slate-700"
      }`}
    >
      {num(v)}
    </td>
  );
  const inp = "border rounded px-1.5 py-0.5 text-right w-32 tabular-nums";

  return (
    <div className="bg-white border rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left">Cuenta</th>
            {COLS_CUENTA.map((c) => (
              <th key={c.k} className="px-3 py-2 text-right whitespace-nowrap">{c.label}</th>
            ))}
            <th className="px-2 py-2 w-14" />
          </tr>
        </thead>
        <tbody>
          {cuentas.map((c) =>
            editando === c.clave ? (
              <tr key={c.clave} className="border-t bg-blue-50/60">
                <td className="px-3 py-1.5 font-medium whitespace-nowrap">{c.clave}</td>
                <td className="px-2 py-1.5 text-right">
                  <input inputMode="decimal" value={saldoIni} onChange={(e) => setSaldoIni(e.target.value)} className={inp} />
                </td>
                {celda(c.ingresos)}
                {celda(c.egresos)}
                <td className="px-3 py-1.5 text-right text-slate-400">—</td>
                <td className="px-2 py-1.5 text-right">
                  <input inputMode="decimal" value={invIni} onChange={(e) => setInvIni(e.target.value)} className={inp} />
                </td>
                {celda(c.abonos)}
                {celda(c.retiros)}
                <td className="px-3 py-1.5 text-right text-slate-400">—</td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <button type="button" title="Guardar" disabled={guardar.isPending} onClick={() => guardar.mutate(c.clave)} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-40 mr-2">
                    <Check className="w-4 h-4" />
                  </button>
                  <button type="button" title="Cancelar" onClick={() => setEditando(null)} className="text-slate-400 hover:text-slate-700">
                    <X className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={c.clave} className={`border-t hover:bg-slate-50 ${fCuenta === c.clave ? "bg-blue-50" : ""}`}>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => onFiltrar(c.clave)}
                    title={fCuenta === c.clave ? "Quitar filtro" : "Ver solo los movimientos de esta cuenta"}
                    className="font-medium text-slate-800 hover:text-blue-700"
                  >
                    {c.clave}
                  </button>
                </td>
                {COLS_CUENTA.map((col) => (
                  <Fragment key={col.k}>{celda(c[col.k] as number, col.fuerte)}</Fragment>
                ))}
                <td className="px-2 py-1.5">
                  <button
                    type="button"
                    title="Editar saldo inicial e inversión inicial"
                    disabled={editando !== null}
                    onClick={() => {
                      setSaldoIni(String(c.saldo_inicial));
                      setInvIni(String(c.inversion_inicial));
                      setEditando(c.clave);
                    }}
                    className="text-slate-400 hover:text-blue-600 disabled:opacity-30"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ),
          )}
        </tbody>
        <tfoot className="border-t-2 border-slate-300 bg-slate-50">
          {monedas.map((mon) => (
            <tr key={mon} className="font-semibold">
              <td className="px-3 py-1.5 whitespace-nowrap">Total {mon}</td>
              {COLS_CUENTA.map((col) => (
                <Fragment key={col.k}>{celda(suma(mon, col.k), true)}</Fragment>
              ))}
              <td />
            </tr>
          ))}
          {/* Todo convertido a MXN: MXN + USD × tipo de cambio. */}
          {tc != null && tc > 0 && (
            <tr className="font-semibold border-t border-slate-300 bg-slate-100">
              <td className="px-3 py-1.5 whitespace-nowrap">
                Total en MXN{" "}
                <Link to="/tipo-cambio" className="font-normal text-xs text-blue-600 hover:underline" title="Editar en Configuración → Tipo de Cambio">
                  (TC {tc.toFixed(2)})
                </Link>
              </td>
              {COLS_CUENTA.map((col) => (
                <Fragment key={col.k}>{celda(suma("MXN", col.k) + suma("USD", col.k) * tc, true)}</Fragment>
              ))}
              <td />
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}

function Chip({ color, n, label }: { color: "emerald" | "amber" | "slate" | "red"; n: number; label: string }) {
  const cls = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    red: "bg-red-50 text-red-700 border-red-200",
  }[color];
  return (
    <span className={`border rounded-full px-3 py-0.5 ${cls}`}>
      <b className="tabular-nums">{n}</b> {label}
    </span>
  );
}

function FilaEdicion({
  nuevo = false,
  b,
  cuentas,
  onChange,
  ocupado,
  onGuardar,
  onCancelar,
}: {
  nuevo?: boolean;
  b: Borrador;
  cuentas: CuentaBancaria[];
  onChange: (b: Borrador) => void;
  ocupado: boolean;
  onGuardar: () => void;
  onCancelar: () => void;
}) {
  const set = (k: keyof Borrador, v: string) => onChange({ ...b, [k]: v });
  const inp = "border rounded px-1.5 py-1 text-sm w-full";
  return (
    <tr
      className="border-t bg-blue-50/60"
      onKeyDown={(e) => {
        if (e.key === "Enter") onGuardar();
        if (e.key === "Escape") onCancelar();
      }}
    >
      <td />
      <td className="px-2 py-1.5">
        {nuevo ? (
          <input value={b.id_movimiento} onChange={(e) => set("id_movimiento", e.target.value)} className={`${inp} font-mono w-24`} />
        ) : (
          <span className="font-mono text-xs">{b.id_movimiento}</span>
        )}
      </td>
      <td className="px-2 py-1.5">
        <input type="date" value={b.fecha_pago} onChange={(e) => set("fecha_pago", e.target.value)} className={inp} />
      </td>
      <td className="px-2 py-1.5 min-w-[200px]">
        <input autoFocus={!nuevo} value={b.concepto} onChange={(e) => set("concepto", e.target.value)} className={inp} />
      </td>
      <td className="px-2 py-1.5">
        <input value={b.factura_relacionada} onChange={(e) => set("factura_relacionada", e.target.value)} className={`${inp} w-24`} />
      </td>
      <td className="px-2 py-1.5">
        <select value={b.tipo} onChange={(e) => set("tipo", e.target.value)} className={inp}>
          <option>Ingreso</option>
          <option>Egreso</option>
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select value={b.inversion} onChange={(e) => set("inversion", e.target.value)} className={inp}>
          <option value="">—</option>
          <option>Abono</option>
          <option>Retiro</option>
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select
          value={b.cuenta}
          onChange={(e) => {
            const c = cuentas.find((x) => x.clave === e.target.value);
            onChange({ ...b, cuenta: e.target.value, moneda: c?.moneda ?? b.moneda });
          }}
          className={inp}
        >
          {cuentas.map((c) => (
            <option key={c.clave} value={c.clave}>{c.clave}</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <input
          inputMode="decimal"
          value={b.monto}
          onChange={(e) => set("monto", e.target.value)}
          className={`${inp} text-right w-32`}
        />
      </td>
      <td className="px-2 py-1.5">
        <select value={b.moneda} onChange={(e) => set("moneda", e.target.value)} className={inp}>
          <option>MXN</option>
          <option>USD</option>
        </select>
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <button type="button" title="Guardar (Enter)" disabled={ocupado} onClick={onGuardar} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-40 mr-2">
          <Check className="w-4 h-4" />
        </button>
        <button type="button" title="Cancelar (Esc)" onClick={onCancelar} className="text-slate-400 hover:text-slate-700">
          <X className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}
